/**
 * budgetCsvParser.js
 * Parses Procountor general ledger accounting reports (Päiväkirja, e.g. kp_raportti_*.csv / .xlsx)
 * Groups transactions by Tositenro and Tositelaji.
 * Ignores default Tili and Tilin nimi from CSV per user requirements.
 * Displays all Vientiselite rows and matches cost centers.
 */
import * as XLSX from "xlsx";

// Default project mapping from CSV "Projekti" column prefixes/names to Cost Center IDs
export const COST_CENTERS = [
  {
    id: "10",
    code: "10",
    name: "Yhdistystoiminta",
    fullName: "10 Yhdistystoiminta",
  },
  { id: "20", code: "20", name: "Hälytysosasto", fullName: "20 Hälytysosasto" },
  {
    id: "30",
    code: "30",
    name: "Nuoriso-osasto",
    fullName: "30 Nuoriso-osasto",
  },
  {
    id: "40",
    code: "40",
    name: "Varhaisnuoriso-osasto",
    fullName: "40 Varhaisnuoriso-osasto",
  },
  { id: "50", code: "50", name: "Tukiosasto", fullName: "50 Tukiosasto" },
  {
    id: "60",
    code: "60",
    name: "Veteraaniosasto",
    fullName: "60 Veteraaniosasto",
  },
  { id: "70", code: "70", name: "Leirikoski", fullName: "70 Leirikoski" },
];

export function matchCostCenter(projectStr) {
  if (!projectStr || typeof projectStr !== "string") return "";
  const trimmed = projectStr.trim();
  const match = trimmed.match(/^(\d{2})/);
  if (match) {
    const code = match[1];
    const found = COST_CENTERS.find((c) => c.code === code);
    if (found) return found.id;
  }
  // Fallback by name keywords
  const lower = trimmed.toLowerCase();
  if (lower.includes("yhdistys")) return "10";
  if (lower.includes("hälytys") || lower.includes("halytys")) return "20";
  if (lower.includes("varhais")) return "40";
  if (lower.includes("nuoriso")) return "30";
  if (lower.includes("tuki") || lower.includes("nais")) return "50";
  if (lower.includes("veteraani")) return "60";
  if (lower.includes("leirikoski") || lower.includes("koulutus")) return "70";
  return "";
}

export function parseFinnishNumber(str) {
  if (str === null || str === undefined) return 0;
  if (typeof str === "number") return str;
  const cleaned = String(str).replace(/\s+/g, "").replace(",", ".");
  const val = parseFloat(cleaned);
  return isNaN(val) ? 0 : val;
}

/**
 * Decodes a File or Blob with fallback to windows-1252 (ISO-8859-1) for Finnish umlauts.
 * If file is an Excel spreadsheet (.xlsx, .xls), converts sheet to semicolon-delimited CSV text.
 */
export async function readCsvFileAsText(file) {
  const buffer = await file.arrayBuffer();
  const fileName = (file && file.name ? file.name : "").toLowerCase();

  // If user provided an Excel file (.xlsx or .xls) from Procountor
  if (fileName.endsWith(".xlsx") || fileName.endsWith(".xls")) {
    try {
      const workbook = XLSX.read(buffer, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      return XLSX.utils.sheet_to_csv(worksheet, { FS: ";" });
    } catch (e) {
      console.warn("Excel parsing failed, trying text decode fallback:", e);
    }
  }

  try {
    // Try windows-1252 / ISO-8859-1 first as Procountor CSVs are typically Latin-1
    const decoder1252 = new TextDecoder("windows-1252", { fatal: false });
    const text1252 = decoder1252.decode(buffer);
    // If text looks good (contains common Finnish characters or keywords without replacement char)
    if (
      text1252.includes("Päiväkirja") ||
      text1252.includes("Vienneittäin") ||
      text1252.includes("Nimi;Tositenro;")
    ) {
      return text1252;
    }
    // Otherwise fallback to UTF-8
    const utf8Decoder = new TextDecoder("utf-8", { fatal: false });
    return utf8Decoder.decode(buffer);
  } catch (e) {
    const fallback = new TextDecoder("utf-8");
    return fallback.decode(buffer);
  }
}

/**
 * Parses raw CSV string into grouped vouchers
 */
export function parseAccountingCsv(csvContent) {
  if (!csvContent) return [];
  const lines = csvContent.split(/\r?\n/).filter((l) => l.trim().length > 0);

  // Find header row starting with Nimi;Tositenro
  const headerIdx = lines.findIndex(
    (l) => l.startsWith("Nimi;Tositenro;") || l.includes(";Tositenro;"),
  );
  if (headerIdx === -1) {
    throw new Error(
      "Tiedostosta ei löytynyt Procountor-otsikkoriviä (Nimi;Tositenro;...).",
    );
  }

  const header = lines[headerIdx].split(";").map((h) => h.trim());
  const rows = [];

  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const cols = line.split(";");
    const rowObj = {};
    header.forEach((h, idx) => {
      rowObj[h] = (cols[idx] || "").trim();
    });
    // Ensure essential fields exist
    if (rowObj["Tositenro"] || rowObj["Nimi"]) {
      const tositelaji = (rowObj["Tositelaji"] || "").trim();
      const tili = (rowObj["Tili"] || "").trim();
      const tilinNimi = (rowObj["Tilin nimi"] || "").toLowerCase();
      const rawVal = parseFinnishNumber(rowObj["Kp-arvo"]);

      // Completely skip "Viitemaksu" and "Tiliote tositteena" rows
      if (
        tositelaji === "Viitemaksu" ||
        tositelaji === "Tiliote tositteena" ||
        tositelaji.toLowerCase().includes("viitemaksu") ||
        tositelaji.toLowerCase().includes("tiliote")
      ) {
        continue;
      }

      // Leave out negative 2880 Maksuliikennetili rows in Ostolasku and Kululasku
      // (in double-entry bookkeeping, 2880 is the payment clearing entry that would cancel expenses to 0)
      const isPaymentClearingLine =
        (tili === "2880" ||
          tili.startsWith("2880") ||
          tilinNimi.includes("maksuliikenne")) &&
        (tositelaji === "Ostolasku" || tositelaji === "Kululasku") &&
        rawVal <= 0;

      if (isPaymentClearingLine) {
        continue;
      }

      // Leave out positive 1700 Myyntisaamiset rows in Myyntilasku
      // (in double-entry bookkeeping, 1700 is the accounts receivable clearing entry)
      const isReceivableClearingLine =
        (tili === "1700" ||
          tili.startsWith("1700") ||
          tilinNimi.includes("myyntisaamiset")) &&
        tositelaji === "Myyntilasku" &&
        rawVal >= 0;

      if (isReceivableClearingLine) {
        continue;
      }

      rows.push(rowObj);
    }
  }

  // Group by Tositelaji + Tositenro
  const groupsMap = new Map();

  rows.forEach((row, rowIdx) => {
    const tositelaji = row["Tositelaji"] || "Muu";
    const tositenro = row["Tositenro"] || `row_${rowIdx}`;
    const groupKey = `${tositelaji}_${tositenro}`;

    if (!groupsMap.has(groupKey)) {
      groupsMap.set(groupKey, {
        groupKey,
        tositelaji,
        tositenro,
        tositepvm: row["Tositepvm"] || "",
        partnerName: row["Nimi"] || "",
        originalProject: "",
        matchedProjectId: "",
        rawRows: [],
      });
    }

    const grp = groupsMap.get(groupKey);
    grp.rawRows.push(row);

    // Pick first non-empty Projekti
    if (!grp.originalProject && row["Projekti"]) {
      grp.originalProject = row["Projekti"];
      grp.matchedProjectId = matchCostCenter(row["Projekti"]);
    }
    // Update date or partner if missing
    if (!grp.tositepvm && row["Tositepvm"]) grp.tositepvm = row["Tositepvm"];
    if (!grp.partnerName && row["Nimi"]) grp.partnerName = row["Nimi"];
  });

  // Build structured vouchers
  const vouchers = [];

  groupsMap.forEach((grp) => {
    const { rawRows } = grp;
    if (!rawRows || rawRows.length === 0) return;

    const bankAccounts = ["1910", "1920", "1990"];

    const isExpenseVoucher =
      grp.tositelaji === "Ostolasku" || grp.tositelaji === "Kululasku";
    const isSalesVoucher = grp.tositelaji === "Myyntilasku";

    let totalExpenseAmount = 0;
    if (isExpenseVoucher) {
      // All remaining rows are genuine expense lines
      const sum = rawRows.reduce(
        (acc, r) => acc + parseFinnishNumber(r["Kp-arvo"]),
        0,
      );
      totalExpenseAmount = -Math.abs(sum);
    } else if (isSalesVoucher) {
      // All remaining rows are genuine revenue lines
      const sum = rawRows.reduce(
        (acc, r) => acc + Math.abs(parseFinnishNumber(r["Kp-arvo"])),
        0,
      );
      totalExpenseAmount = Math.abs(sum);
    } else {
      // Bank statements / reference payments
      const nonBankLines = rawRows.filter(
        (r) => !bankAccounts.includes(r["Tili"]) && r["Tili"] !== "2880",
      );
      if (nonBankLines.length > 0) {
        const sum = nonBankLines.reduce(
          (acc, r) => acc + parseFinnishNumber(r["Kp-arvo"]),
          0,
        );
        totalExpenseAmount = -Math.abs(sum);
      } else {
        const payLine = rawRows.find((r) => r["Tili"] === "2880");
        if (payLine) {
          totalExpenseAmount = parseFinnishNumber(payLine["Kp-arvo"]);
        } else {
          totalExpenseAmount = parseFinnishNumber(rawRows[0]["Kp-arvo"]);
        }
      }
    }

    // Extract itemized lines (showing Vientiselite)
    const items = rawRows.map((r, idx) => {
      const rawAmt = parseFinnishNumber(r["Kp-arvo"]);
      let amt = rawAmt;
      if (isExpenseVoucher) {
        amt = -Math.abs(rawAmt);
      } else if (isSalesVoucher) {
        amt = Math.abs(rawAmt);
      }
      return {
        id: `${grp.groupKey}_item_${idx}`,
        vientiselite: r["Vientiselite"] || r["Tilin nimi"] || "",
        amount: Math.round(amt * 100) / 100,
        alvPercent: r["ALV-%"] || "",
        alvAmount: parseFinnishNumber(r["ALV"]),
        alvStatus: r["ALV-status"] || "",
        originalTili: r["Tili"] || "",
      };
    });

    // Completely skip pure bank settlements if any remain
    const isPureBankSettlement = rawRows.every(
      (r) => bankAccounts.includes(r["Tili"]) || r["Tili"] === "2880",
    );
    if (isPureBankSettlement) {
      return;
    }

    vouchers.push({
      id: grp.groupKey,
      groupKey: grp.groupKey,
      tositelaji: grp.tositelaji,
      tositenro: grp.tositenro,
      tositepvm: grp.tositepvm,
      partnerName: grp.partnerName,
      originalProject: grp.originalProject,
      projectId: grp.matchedProjectId || "",
      totalAmount: Math.round(totalExpenseAmount * 100) / 100,
      items,
      status: "pending", // "pending" | "assigned" | "declined"
      declineReason: "",
      selectedAccountId: "",
      selectedAccountName: "",
      selectedAccountNumber: "",
    });
  });

  return vouchers;
}
